'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import { strategyCopy } from '@/lib/strategy';

/** S13 币池选择页（文档 V2.0 P199-P200 / 参照图 image25-26；P200 资格状态机 ELIGIBLE/INSUFFICIENT_HISTORY/DATA_GAP/UNSUPPORTED_MARKET/DELISTED_IN_WINDOW） */
export interface UniverseMarket {
  market: string;
  exchange: string;
  type: string;
  coverageLabel: string;
  coveragePct: number | null;
  eligible: boolean;
  status: 'ELIGIBLE' | 'INSUFFICIENT_HISTORY' | 'DATA_GAP' | 'UNSUPPORTED_MARKET' | 'DELISTED_IN_WINDOW';
  failReason: string | null;
}

const STATUS_META: Record<UniverseMarket['status'], { zh: string; en: string; cls: string; dot: string }> = {
  ELIGIBLE: { zh: '可回测', en: 'Eligible', cls: 'bg-gf-up/15 text-gf-up', dot: 'bg-gf-up/70' },
  INSUFFICIENT_HISTORY: { zh: '历史不足', en: 'Insufficient history', cls: 'bg-[#D97706]/15 text-[#D97706]', dot: 'bg-[#D97706]/70' },
  DATA_GAP: { zh: '数据缺口', en: 'Data gap', cls: 'bg-[#D97706]/15 text-[#D97706]', dot: 'bg-[#D97706]/70' },
  UNSUPPORTED_MARKET: { zh: '市场不支持', en: 'Unsupported market', cls: 'bg-gf-muted/20 text-gf-muted', dot: 'bg-gf-muted/50' },
  DELISTED_IN_WINDOW: { zh: '窗口内已下架', en: 'Delisted in window', cls: 'bg-gf-down/15 text-gf-down', dot: 'bg-gf-down/70' },
};

export interface SavedUniverse {
  id: string;
  name: string;
  type: string;
  members: string[];
  rule: Record<string, unknown> | null;
  timezone: string;
  versionHash: string;
  createdAt: string;
}

const UNIVERSE_DRAFT_KEY = 'ailumix_universe_draft_v1';
const PERIOD_MS = 90 * 86_400_000;

export default function UniversesPage() {
  const router = useRouter();
  const params = useSearchParams();
  const templateKey = params.get('templateKey') ?? 't1';
  const batchMode = params.get('batch') === '1';
  const { language } = useAppStore();
  const zh = language !== 'en';
  const t = strategyCopy(zh);
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<'manual' | 'dynamic' | 'saved'>('manual');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [name, setName] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [dynN, setDynN] = useState(5);
  const [dynSort, setDynSort] = useState('market');
  const [loadedUniverse, setLoadedUniverse] = useState<SavedUniverse | null>(null);
  const [batchShared, setBatchShared] = useState(false);

  const { data: markets, isLoading, isError } = useQuery({
    queryKey: ['strategy-markets', q.trim().toUpperCase()],
    queryFn: async () => {
      const endMs = Date.now();
      const qs = new URLSearchParams({ startMs: String(endMs - PERIOD_MS), endMs: String(endMs) });
      if (q.trim()) qs.set('query', q.trim().toUpperCase());
      const r = await api.get<{ window: { startMs: number; endMs: number }; markets: UniverseMarket[] }>(`/strategy/markets?${qs.toString()}`);
      return r;
    },
  });

  const { data: saved } = useQuery({
    queryKey: ['strategy-universes'],
    queryFn: async () => api.get<SavedUniverse[]>('/strategy/universes'),
    enabled: tab === 'saved',
  });

  const startBatch = useMutation({
    mutationFn: async () => {
      const endMs = Date.now();
      const r = await api.post<{ id: string }>('/strategy/batches', {
        templateKey,
        symbols: [...selected],
        mode: batchShared ? 'SHARED' : 'INDEPENDENT',
        capital: 10_000,
        startMs: endMs - PERIOD_MS,
        endMs,
      });
      return r.id;
    },
    onSuccess: (id) => {
      router.push(`/strategy/batches/${id}`);
    },
    onError: () => setMsg(zh ? '批量任务创建失败（含不符合资格市场），请调整后重试' : 'Batch creation failed (may contain ineligible markets), adjust and retry'),
  });

  const saveUniverse = useMutation({
    mutationFn: async () => {
      const members = [...selected];
      const type = tab === 'dynamic' ? 'DYNAMIC' : tab === 'saved' && loadedUniverse ? 'SAVED' : 'STATIC_MANUAL';
      const r = await api.post<{ id: string; versionHash: string }>('/strategy/universes', {
        name: name.trim() || undefined,
        type,
        members,
        rule: tab === 'dynamic' ? { sortBy: dynSort, takeN: dynN } : null,
      });
      return r;
    },
    onSuccess: (r) => {
      // 确认通过后写入草稿（返回策略配置时自动带入）
      try {
        window.sessionStorage.setItem(UNIVERSE_DRAFT_KEY, JSON.stringify({ members: [...selected], versionHash: r.versionHash, at: Date.now() }));
      } catch { /* noop */ }
      setMsg(zh ? '币池已保存并锁定成员版本' : 'Pool saved with member version locked');
      router.push(`/strategy/${templateKey}?symbols=${[...selected].join(',')}`);
    },
    onError: () => setMsg(zh ? '保存失败，请重试' : 'Failed to save, retry'),
  });

  const deleteUniverse = useMutation({
    mutationFn: async (id: string) => api.post<{ ok: boolean }>(`/strategy/universes/${id}/delete`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategy-universes'] });
    },
  });

  // 动态池：按字母序取前 N
  useEffect(() => {
    if (tab !== 'dynamic') return;
    const list = [...(markets?.markets ?? [])].sort((a, b) => a.market.localeCompare(b.market));
    setSelected(new Set(list.slice(0, Math.min(dynN, list.length)).map((m) => m.market)));
  }, [tab, dynN, markets]);

  const rows = useMemo(() => markets?.markets ?? [], [markets]);
  const selList = [...selected];
  const selInvalid = selList.filter((s) => !rows.find((r) => r.market === s)?.eligible);

  const confirmDisabled = selList.length < 2 || saveUniverse.isPending || startBatch.isPending || selInvalid.length > 0;
  const confirmBusy = saveUniverse.isPending || startBatch.isPending;
  const confirmLabel = batchMode ? t.startBatch : t.checkConfirmUniverse;

  const badge = (m: UniverseMarket) => {
    const meta = STATUS_META[m.status] ?? STATUS_META.UNSUPPORTED_MARKET;
    return (
      <span
        className={`cursor-help rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}
        title={m.failReason ?? undefined}
      >
        {zh ? meta.zh : meta.en}
      </span>
    );
  };

  const coverage = (m: UniverseMarket) => {
    if (m.eligible) return <span className="text-sm font-medium text-gf-up">{t.coverageFull}</span>;
    const label = m.coverageLabel === 'full' ? t.coverageFull : m.coverageLabel === 'partial' ? t.coveragePartial : t.coverageNone;
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm text-gf-muted">{label}</span>
        {!m.eligible && (
          <span className="cursor-help text-xs text-gf-down/80" title={m.failReason ?? ''}>
            · {t.viewReason}
          </span>
        )}
      </span>
    );
  };

  const statusLegend = (['ELIGIBLE', 'INSUFFICIENT_HISTORY', 'DATA_GAP', 'UNSUPPORTED_MARKET', 'DELISTED_IN_WINDOW'] as const).map((k) => (
    <div key={k} className="flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${STATUS_META[k].dot}`} />
      {zh ? STATUS_META[k].zh : STATUS_META[k].en}
    </div>
  ));

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-36 pt-5 md:pb-10">
      {/* 标题 */}
      <div className="mb-4 flex items-baseline gap-3">
        <h1 className="text-xl font-bold text-gf-text md:text-2xl">{t.universeTitle}</h1>
        <span className="hidden text-sm text-gf-muted md:inline">{t.universeSub}</span>
      </div>

      {/* tabs */}
      <div className="mb-4 flex gap-2 overflow-x-auto">
        {([['manual', t.tabManual], ['dynamic', t.tabDynamic], ['saved', t.tabSaved]] as const).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => { setTab(k); setLoadedUniverse(null); }}
            className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${
              tab === k ? 'border-gf-accent bg-gf-accent/15 font-medium text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'saved' ? (
        /* 已保存币池 */
        <div className="space-y-3">
          {saved && saved.length === 0 && (
            <div className="rounded-2xl border border-dashed border-gf-border bg-gf-surface2 p-10 text-center text-sm text-gf-muted">{t.universeEmpty}</div>
          )}
          {saved?.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gf-border bg-gf-surface p-4">
              <div>
                <div className="font-medium text-gf-text">{u.name}</div>
                <div className="mt-1 text-xs text-gf-muted">
                  {u.members.join(' · ')}
                  <span className="ml-2 rounded bg-gf-surface2 px-1.5 py-0.5 text-[11px]">{u.type === 'DYNAMIC' ? t.universeTypeDynamic : t.universeTypeStatic}</span>
                  <span className="ml-1.5 text-[11px]">rev{u.versionHash.slice(0, 6)}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setSelected(new Set(u.members)); setLoadedUniverse(u); setTab('manual'); }}
                  className="rounded-lg border border-gf-border bg-gf-surface2 px-3 py-1.5 text-sm font-medium text-gf-text"
                >
                  {zh ? '载入' : 'Load'}
                </button>
                <button
                  type="button"
                  onClick={() => deleteUniverse.mutate(u.id)}
                  className="rounded-lg border border-gf-border bg-gf-surface2 px-3 py-1.5 text-sm text-gf-down"
                >
                  {t.universeDeleteOk}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[300px_1fr_320px]">
          {/* 左：搜索与资格筛选 */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-gf-border bg-gf-surface p-4">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t.universeSearchPh}
                className="w-full rounded-lg border border-gf-border bg-gf-surface2 px-3 py-2.5 text-sm text-gf-text placeholder:text-gf-muted focus:border-gf-accent/60 focus:outline-none"
              />
              <div className="mt-3 space-y-1 text-xs text-gf-muted">{statusLegend}</div>
            </div>
            <div className="hidden rounded-2xl border border-gf-border bg-gf-surface p-4 lg:block">
              {tab === 'dynamic' ? (
                <div className="space-y-3 text-sm">
                  <div className="font-medium text-gf-text">{t.dynamicRuleLabel}</div>
                  <p className="text-xs leading-relaxed text-gf-muted">{t.dynamicRuleDesc}</p>
                  <label className="block">
                    <span className="mb-1 block text-xs text-gf-muted">{t.dynamicSortBy}</span>
                    <select
                      value={dynSort}
                      onChange={(e) => setDynSort(e.target.value)}
                      className="w-full rounded-lg border border-gf-border bg-gf-surface2 px-3 py-2 text-sm text-gf-text focus:outline-none"
                    >
                      <option value="market">Market (A–Z)</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-gf-muted">{t.dynamicTakeN}</span>
                    <input
                      type="number"
                      min={2}
                      max={20}
                      value={dynN}
                      onChange={(e) => setDynN(Math.min(20, Math.max(2, Number(e.target.value) || 2)))}
                      className="w-full rounded-lg border border-gf-border bg-gf-surface2 px-3 py-2 text-sm text-gf-text focus:outline-none"
                    />
                  </label>
                </div>
              ) : (
                <div className="space-y-2 text-xs text-gf-muted">
                  <div className="font-medium text-gf-text">{zh ? '资格筛选' : 'Eligibility filter'}</div>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="accent-gf-accent" defaultChecked readOnly />
                    {zh ? '仅显示可回测市场' : 'Eligible markets only'}
                  </label>
                  <p className="pt-1 leading-relaxed">{t.eligibilityNote}</p>
                </div>
              )}
            </div>
          </div>

          {/* 中：市场结果 */}
          <div className="rounded-2xl border border-gf-border bg-gf-surface">
            <div className="hidden grid-cols-[1fr_1.2fr_0.8fr] gap-2 border-b border-gf-border px-4 py-3 text-xs font-medium text-gf-muted md:grid">
              <span>{t.colMarket}</span><span>{t.colCoverage}</span><span>{t.colEligible}</span>
            </div>
            {isLoading && <div className="p-8 text-center text-sm text-gf-muted">{t.loading}</div>}
            {isError && <div className="p-8 text-center text-sm text-gf-down">{zh ? '载入失败' : 'Failed to load'}</div>}
            {!isLoading && !isError && rows.length === 0 && (
              <div className="p-8 text-center text-sm text-gf-muted">{t.searchNoResult}</div>
            )}
            {rows.map((m) => {
              const on = selected.has(m.market);
              return (
                <button
                  key={m.market}
                  type="button"
                  disabled={!m.eligible}
                  onClick={() => {
                    const next = new Set(selected);
                    if (on) next.delete(m.market);
                    else if (next.size < 20) next.add(m.market);
                    setSelected(next);
                  }}
                  className={`flex w-full items-center justify-between gap-2 border-b border-gf-border px-4 py-3 text-left transition-colors last:border-b-0 md:grid md:grid-cols-[1fr_1.2fr_0.8fr] md:items-center ${
                    on ? 'bg-gf-accent/10' : 'hover:bg-gf-surface2'
                  } ${!m.eligible ? 'opacity-60' : ''}`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${on ? 'bg-gf-accent' : 'bg-gf-muted/40'}`} />
                    <span className="font-medium text-gf-text">{m.market}</span>
                    <span className="hidden text-xs text-gf-muted md:inline">Binance · {m.type}</span>
                    <span className={`text-xs md:hidden ${on ? 'text-gf-accent' : 'text-gf-muted'}`}>
                      {on ? t.selectedCoin : m.eligible ? zh ? '可选' : 'Selectable' : ''}
                    </span>
                  </span>
                  <span className="hidden md:block">{coverage(m)}</span>
                  <span className="hidden md:block">{badge(m)}</span>
                  {/* 手机卡样式 */}
                  <span className="md:hidden">
                    {m.eligible ? (
                      <span className="text-xs text-gf-up">{t.historyFullEligible}</span>
                    ) : (
                      <span className="cursor-help text-xs text-gf-down/80" title={m.failReason ?? ''}>
                        {zh ? STATUS_META[m.status]?.zh : STATUS_META[m.status]?.en}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
            <div className="border-t border-gf-border px-4 py-3 text-xs text-gf-muted md:hidden">{t.eligibilityNote}</div>
          </div>

          {/* 右：已选与确认 */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-gf-border bg-gf-surface p-4">
              <div className="mb-3 text-sm font-semibold text-gf-text">
                {t.selectedCount.replace('{n}', String(selList.length))}
              </div>
              {selList.length === 0 && <div className="text-xs text-gf-muted">{zh ? '从左侧选择市场' : 'Select markets from the list'}</div>}
              <ul className="space-y-1.5">
                {selList.map((s) => (
                  <li key={s} className="flex items-center justify-between rounded-lg bg-gf-surface2 px-3 py-2 text-sm">
                    <span className="font-medium text-gf-text">{s}</span>
                    <span className="flex items-center gap-2">
                      {!rows.find((r) => r.market === s)?.eligible && <span className="text-xs text-gf-down">{t.eligibleNo}</span>}
                      <span className="text-xs text-gf-muted">{zh ? '现货' : 'Spot'}</span>
                      <button type="button" className="text-gf-muted" onClick={() => { const n = new Set(selected); n.delete(s); setSelected(n); }}>✕</button>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 border-t border-gf-border pt-3 text-xs text-gf-muted">
                <div>{tab === 'dynamic' ? t.universeTypeDynamic : t.universeTypeStatic}</div>
                <div className="mt-1 flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-gf-accent" />
                  {t.lockMembersOnSave}
                </div>
              </div>
            </div>

            {selInvalid.length > 0 && (
              <div className="rounded-2xl border border-gf-down/40 bg-gf-down/10 p-3 text-xs text-gf-down">
                {zh ? '以下市场不具备资格：' : 'Not eligible:'} {selInvalid.join(', ')}
              </div>
            )}

            {/* S14 批量模式：独立 / 共享（文档 P202 切共享模式需创建新任务，不复用独立结果冒充组合） */}
            {batchMode && (
              <div className="rounded-2xl border border-gf-border bg-gf-surface p-4">
                <div className="mb-2 text-sm font-semibold text-gf-text">{t.sharedMode}</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBatchShared(false)}
                    className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors ${
                      !batchShared ? 'border-gf-accent bg-gf-accent/15 text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted'
                    }`}
                  >
                    {t.independentMode}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBatchShared(true)}
                    className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors ${
                      batchShared ? 'border-gf-accent bg-gf-accent/15 text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted'
                    }`}
                  >
                    {t.sharedMode}
                  </button>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-gf-muted">
                  {batchShared ? t.sharedBatchNote : t.perCoinBudget}
                </p>
              </div>
            )}

            <div className="hidden rounded-2xl border border-gf-border bg-gf-surface p-4 lg:block">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.savedUniverseName}
                className="mb-3 w-full rounded-lg border border-gf-border bg-gf-surface2 px-3 py-2 text-sm text-gf-text placeholder:text-gf-muted focus:outline-none"
              />
              <button
                type="button"
                disabled={confirmDisabled}
                onClick={() => (batchMode ? startBatch.mutate() : saveUniverse.mutate())}
                className={`w-full rounded-xl py-3 text-sm font-semibold ${
                  confirmDisabled ? 'cursor-not-allowed bg-gf-muted/20 text-gf-muted' : 'bg-gf-accent text-white hover:bg-gf-accent/90'
                }`}
              >
                {confirmBusy ? '…' : confirmLabel}
              </button>
              {!batchMode && (
                <Link
                  href={`/strategy/${templateKey}`}
                  className="mt-2 block text-center text-xs text-gf-muted hover:text-gf-text"
                >
                  {t.backToStrategy}
                </Link>
              )}
              {msg && <p className="mt-2 text-center text-xs text-gf-up">{msg}</p>}
            </div>
          </div>
        </div>
      )}

      {/* 手机底部确认栏 */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gf-border bg-gf-surface/95 px-4 py-3 backdrop-blur lg:hidden">
        <button
          type="button"
          disabled={confirmDisabled}
          onClick={() => (batchMode ? startBatch.mutate() : saveUniverse.mutate())}
          className={`w-full rounded-xl py-3 text-sm font-semibold ${
            confirmDisabled ? 'cursor-not-allowed bg-gf-muted/20 text-gf-muted' : 'bg-gf-accent text-white'
          }`}
        >
          {confirmBusy ? '…' : `${zh ? '已选' : 'Selected'} ${selList.length}${zh ? '币·' : ' · '}${confirmLabel}`}
        </button>
        {msg && <p className="mt-1 text-center text-xs text-gf-up">{msg}</p>}
      </div>
    </div>
  );
}
